import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth, useUi, useWorkspace } from "@/lib/store";
import { useLocalization } from "@/lib/localization";
import { hasPermission } from "@/lib/permissions";
import { SignOutConfirmationDialog } from "@/components/auth/SignOutConfirmationDialog";
import {
  LayoutDashboard, ShoppingCart, Boxes, ReceiptText, Wallet, BarChart3,
  Bell, Sparkles, Users, Building2, Settings, Sun, Moon, LogOut, Plus, Search,
  Landmark, Coins, FileText,
} from "lucide-react";

export default function CommandPalette() {
  const { commandOpen, setCommandOpen, toggleTheme, theme } = useUi();
  const { session } = useAuth();
  const { switchBranch, branches, products } = useWorkspace();
  const { messages } = useLocalization();
  const copy = messages.layout;
  const navigate = useNavigate();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const can = (permission) => hasPermission(session, permission);
  const canBrowseProducts = can("inventory") || can("sell");

  const go = (path) => {
    setCommandOpen(false);
    navigate(path);
  };

  return (
    <>
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <CommandInput data-testid="command-palette-input" placeholder={copy.commandPlaceholder} />
        <CommandList>
        <CommandEmpty>{copy.noResults}</CommandEmpty>

        <CommandGroup heading={copy.quickActions}>
          {can("sell") ? <CommandItem data-testid="cmd-new-sale" onSelect={() => go("/sell")}>
            <Plus className="h-4 w-4" />
            <span>{copy.startSale}</span>
            <CommandShortcut>S</CommandShortcut>
          </CommandItem> : null}
          {can("inventory") ? <CommandItem data-testid="cmd-search-inventory" onSelect={() => go("/inventory")}>
            <Search className="h-4 w-4" />
            <span>{copy.searchInventory}</span>
          </CommandItem> : null}
          {can("assistant") ? <CommandItem data-testid="cmd-ask-assistant" onSelect={() => go("/assistant")}>
            <Sparkles className="h-4 w-4" />
            <span>{copy.askAssistant}</span>
          </CommandItem> : null}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={copy.navigate}>
          {can("analytics") ? <CommandItem onSelect={() => go("/dashboard")}><LayoutDashboard className="h-4 w-4" /> {copy.dashboard}</CommandItem> : null}
          {can("sell") ? <CommandItem onSelect={() => go("/sell")}><ShoppingCart className="h-4 w-4" /> {copy.sell}</CommandItem> : null}
          {can("inventory") ? <CommandItem onSelect={() => go("/inventory")}><Boxes className="h-4 w-4" /> {copy.inventory}</CommandItem> : null}
          {can("history") ? <CommandItem onSelect={() => go("/history")}><ReceiptText className="h-4 w-4" /> {copy.history}</CommandItem> : null}
          {can("debts") ? <CommandItem onSelect={() => go("/debts")}><Wallet className="h-4 w-4" /> {copy.debts}</CommandItem> : null}
          {can("expenses") ? <CommandItem onSelect={() => go("/expenses")}><Landmark className="h-4 w-4" /> {copy.expenses}</CommandItem> : null}
          <CommandItem onSelect={() => go("/credits")}><Coins className="h-4 w-4" /> {copy.credits}</CommandItem>
          {can("analytics") ? <CommandItem onSelect={() => go("/analytics")}><BarChart3 className="h-4 w-4" /> {copy.analytics}</CommandItem> : null}
          {can("analytics") ? <CommandItem onSelect={() => go("/reports")}><FileText className="h-4 w-4" /> {copy.reports}</CommandItem> : null}
          {can("notifications") ? <CommandItem onSelect={() => go("/notifications")}><Bell className="h-4 w-4" /> {copy.notifications}</CommandItem> : null}
          {can("staff_access") ? <CommandItem onSelect={() => go("/staff")}><Users className="h-4 w-4" /> {copy.staff}</CommandItem> : null}
          {can("branch_management") ? <CommandItem onSelect={() => go("/branches")}><Building2 className="h-4 w-4" /> {copy.branches}</CommandItem> : null}
          {can("settings") ? <CommandItem onSelect={() => go("/settings")}><Settings className="h-4 w-4" /> {copy.settings}</CommandItem> : null}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading={copy.switchBranch}>
          {branches.map((b) => (
            <CommandItem key={b.id} onSelect={() => { switchBranch(b.id); setCommandOpen(false); }}>
              <Building2 className="h-4 w-4" />
              {b.name} <span className="text-muted-foreground">· {b.city}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {canBrowseProducts ? <CommandGroup heading={copy.products}>
          {products.slice(0, 6).map((p) => (
            <CommandItem key={p.id} onSelect={() => go(can("inventory") ? "/inventory" : "/sell")}>
              <Boxes className="h-4 w-4" />
              {p.name}
              <span className="ml-auto font-mono text-xs text-muted-foreground">{p.stock ?? "—"} {copy.inStock}</span>
            </CommandItem>
          ))}
        </CommandGroup> : null}

        <CommandSeparator />

        <CommandGroup heading={copy.preferences}>
          <CommandItem onSelect={() => { toggleTheme(); setCommandOpen(false); }}>
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {copy.toggleTheme(theme === "dark" ? "light" : "dark")}
          </CommandItem>
          <CommandItem onSelect={() => { setCommandOpen(false); setSignOutOpen(true); }}>
            <LogOut className="h-4 w-4" /> {copy.signOut}
          </CommandItem>
        </CommandGroup>
        </CommandList>
      </CommandDialog>
      <SignOutConfirmationDialog open={signOutOpen} onOpenChange={setSignOutOpen} />
    </>
  );
}
